import { formatSwedishDate } from "../../helpers/DateTime";
import i18n from "../../i18n/i18n";

export function toUtcMidnight(dateStr) {
    if (!dateStr) {
        return null;
    }
    return `${dateStr}T00:00:00.000Z`;
}

export function isoToDateInputValue(iso) {
    if (!iso) {
        return "";
    }

    const match = /^(\d{4}-\d{2}-\d{2})/.exec(String(iso).trim());
    return match ? match[1] : "";
}

export function utcToSwedishDateInputValue(iso) {
    if (!iso) {
        return "";
    }

    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) {
        return isoToDateInputValue(iso);
    }

    const parts = new Intl.DateTimeFormat("sv-SE", {
        timeZone: "Europe/Stockholm",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
    }).formatToParts(date);

    const year = parts.find((part) => part.type === "year")?.value;
    const month = parts.find((part) => part.type === "month")?.value;
    const day = parts.find((part) => part.type === "day")?.value;

    if (!year || !month || !day) {
        return isoToDateInputValue(iso);
    }

    return `${year}-${month}-${day}`;
}

export function formatDateOnly(iso) {
    if (!iso) {
        return "—";
    }

    const dateOnly = isoToDateInputValue(iso);
    return dateOnly || "—";
}

export function formatTariffValidDate(iso) {
    if (!iso) {
        return "—";
    }

    const formatted = formatSwedishDate(iso);
    if (!formatted || formatted.includes("�")) {
        return "—";
    }

    return formatted;
}

function formatGroupedNumber(value, fractionDigits) {
    const n = Number(value);
    if (!Number.isFinite(n)) {
        return null;
    }

    const [intPart, decPart] = n.toFixed(fractionDigits).split(".");
    const groupedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, " ");
    return decPart !== undefined ? `${groupedInt}.${decPart}` : groupedInt;
}

export function normalizeDecimalInput(value) {
    if (value == null) {
        return "";
    }

    let cleaned = String(value).trim().replace(/\s/g, "");
    if (cleaned === "") {
        return "";
    }

    const lastComma = cleaned.lastIndexOf(",");
    const lastDot = cleaned.lastIndexOf(".");

    if (lastComma > -1 && lastDot > -1) {
        if (lastComma > lastDot) {
            cleaned = cleaned.replace(/\./g, "").replace(",", ".");
        } else {
            cleaned = cleaned.replace(/,/g, "");
        }
    } else {
        cleaned = cleaned.replace(",", ".");
    }

    cleaned = cleaned.replace(/[^0-9.-]/g, "");

    const parts = cleaned.split(".");
    if (parts.length > 2) {
        cleaned = `${parts[0]}.${parts.slice(1).join("")}`;
    }

    if (cleaned.includes("-")) {
        cleaned = `-${cleaned.replace(/-/g, "")}`;
    }

    return cleaned;
}

export function decimalToInputString(value) {
    if (value == null || value === "") {
        return "";
    }

    const normalized = normalizeDecimalInput(String(value));
    if (normalized === "" || normalized === "-" || normalized === ".") {
        return normalized;
    }

    return normalized;
}

export function formatSek(value) {
    if (value == null || Number.isNaN(Number(value))) {
        return "—";
    }
    return `${formatGroupedNumber(value, 2)} SEK`;
}

export function formatKw(value) {
    if (value == null || Number.isNaN(Number(value))) {
        return "—";
    }
    return `${formatGroupedNumber(value, 1)} kW`;
}

export function formatKwh(value) {
    if (value == null || Number.isNaN(Number(value))) {
        return "—";
    }
    return `${formatGroupedNumber(value, 1)} kWh`;
}

export function formatDecimal(value, fractionDigits = 4) {
    if (value == null || Number.isNaN(Number(value))) {
        return "—";
    }
    return formatGroupedNumber(value, fractionDigits);
}

export function formatSpotPricePerKwh(value) {
    if (value == null || Number.isNaN(Number(value))) {
        return "—";
    }
    return `${formatGroupedNumber(value, 4)} SEK/kWh`;
}

function getDisplayLocale() {
    const language = i18n.resolvedLanguage || i18n.language || "en";
    return language.startsWith("sv") ? "sv-SE" : "en-GB";
}

export function formatYearMonthLabel(yearMonth) {
    const match = /^(\d{4})-(\d{2})$/.exec(String(yearMonth || "").trim());
    if (!match) {
        return yearMonth || "—";
    }

    const year = Number(match[1]);
    const month = Number(match[2]);
    if (month < 1 || month > 12) {
        return yearMonth;
    }

    const date = new Date(year, month - 1, 1);
    const formatted = date.toLocaleString(getDisplayLocale(), { month: "long", year: "numeric" });
    if (!formatted) {
        return yearMonth;
    }

    return formatted.charAt(0).toUpperCase() + formatted.slice(1);
}

export function formatSpotAddonPercent(value) {
    if (value == null || Number.isNaN(Number(value))) {
        return "—";
    }
    const n = Number(value);
    const pct = n <= 1 && n >= -1 ? n * 100 : n;
    return `${formatGroupedNumber(pct, 2)}%`;
}

export function parseYearMonth(ym) {
    const match = /^(\d{4})-(\d{2})$/.exec(String(ym || "").trim());
    if (!match) {
        return null;
    }
    const year = Number(match[1]);
    const month = Number(match[2]);
    if (month < 1 || month > 12) {
        return null;
    }
    return { year, month };
}

export function expandYearMonths(fromYm, toYm) {
    const from = parseYearMonth(fromYm);
    const to = parseYearMonth(toYm);
    if (!from || !to) {
        return [];
    }

    let y = from.year;
    let m = from.month;
    const endY = to.year;
    const endM = to.month;
    const result = [];

    while (y < endY || (y === endY && m <= endM)) {
        result.push(`${y}-${String(m).padStart(2, "0")}`);
        m += 1;
        if (m > 12) {
            m = 1;
            y += 1;
        }
    }

    return result;
}

function getStockholmYearMonthParts(date = new Date()) {
    const parts = new Intl.DateTimeFormat("en-GB", {
        timeZone: "Europe/Stockholm",
        year: "numeric",
        month: "numeric",
    }).formatToParts(date);

    const year = Number(parts.find((part) => part.type === "year")?.value);
    const month = Number(parts.find((part) => part.type === "month")?.value);

    if (!Number.isFinite(year) || !Number.isFinite(month)) {
        return null;
    }

    return { year, month };
}

export function getLatestAllowedYearMonth() {
    const stockholm = getStockholmYearMonthParts();
    if (!stockholm) {
        return currentYearMonthLocal();
    }

    let { year, month } = stockholm;
    month -= 1;
    if (month < 1) {
        month = 12;
        year -= 1;
    }

    return `${year}-${String(month).padStart(2, "0")}`;
}

function currentYearMonthLocal() {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}

export function compareYearMonth(a, b) {
    return String(a || "").localeCompare(String(b || ""));
}

export function clampYearMonth(value, maxYearMonth) {
    if (!value) {
        return value;
    }

    if (!maxYearMonth) {
        return value;
    }

    return compareYearMonth(value, maxYearMonth) > 0 ? maxYearMonth : value;
}

export function addOneYearMonth(yearMonth) {
    const parsed = parseYearMonth(yearMonth);
    if (!parsed) {
        return "";
    }

    let { year, month } = parsed;
    month += 1;
    if (month > 12) {
        month = 1;
        year += 1;
    }

    return `${year}-${String(month).padStart(2, "0")}`;
}

export function subtractOneYearMonth(yearMonth) {
    const parsed = parseYearMonth(yearMonth);
    if (!parsed) {
        return "";
    }

    let { year, month } = parsed;
    month -= 1;
    if (month < 1) {
        month = 12;
        year -= 1;
    }

    return `${year}-${String(month).padStart(2, "0")}`;
}

export function utcToTariffValidFromMonth(iso) {
    const dateOnly = utcToSwedishDateInputValue(iso);
    if (!dateOnly) {
        return "";
    }

    return dateOnly.substring(0, 7);
}

export function utcToTariffValidToInclusiveMonth(iso) {
    if (!iso) {
        return "";
    }

    const dateOnly = utcToSwedishDateInputValue(iso);
    if (!dateOnly) {
        return "";
    }

    const yearMonth = dateOnly.substring(0, 7);
    const day = Number(dateOnly.substring(8, 10));
    if (Number.isNaN(day) || day === 1) {
        return subtractOneYearMonth(yearMonth);
    }

    return yearMonth;
}

export function tariffValidFromMonthToUtc(yearMonth) {
    const parsed = parseYearMonth(yearMonth);
    if (!parsed) {
        return null;
    }

    const day = `${parsed.year}-${String(parsed.month).padStart(2, "0")}-01`;
    return toUtcMidnight(day);
}

export function tariffValidToInclusiveMonthToUtc(yearMonth) {
    if (!yearMonth) {
        return null;
    }

    const nextMonth = addOneYearMonth(yearMonth);
    const parsed = parseYearMonth(nextMonth);
    if (!parsed) {
        return null;
    }

    const day = `${parsed.year}-${String(parsed.month).padStart(2, "0")}-01`;
    return toUtcMidnight(day);
}

export function validateTariffMonthRange(fromMonth, toMonth) {
    if (!fromMonth) {
        return i18n.t("gridCost.selectValidFromMonth");
    }

    if (toMonth && compareYearMonth(fromMonth, toMonth) > 0) {
        return i18n.t("gridCost.validToAfterFrom");
    }

    return null;
}

export function formatTariffValidFromMonthDisplay(iso) {
    const yearMonth = utcToTariffValidFromMonth(iso);
    return yearMonth ? formatYearMonthLabel(yearMonth) : "—";
}

export function formatTariffValidToMonthDisplay(iso) {
    if (!iso) {
        return i18n.t("gridCost.openEnded");
    }

    const yearMonth = utcToTariffValidToInclusiveMonth(iso);
    return yearMonth ? formatYearMonthLabel(yearMonth) : "—";
}

export function validatePastYearMonthRange(fromYm, toYm, maxYearMonth) {
    if (!fromYm || !toYm) {
        return i18n.t("gridCost.selectFromToMonth");
    }

    if (compareYearMonth(fromYm, toYm) > 0) {
        return i18n.t("gridCost.fromBeforeTo");
    }

    if (compareYearMonth(fromYm, maxYearMonth) > 0 || compareYearMonth(toYm, maxYearMonth) > 0) {
        return i18n.t("gridCost.onlyCompletedSelected", { month: maxYearMonth });
    }

    const months = expandYearMonths(fromYm, toYm);
    if (months.length === 0) {
        return i18n.t("gridCost.invalidMonthRange");
    }

    return null;
}
